'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  getToken,
  decodeToken,
  isTokenExpired,
  clearSession,
  isAuthenticated as checkAuth,
  hasACL,
  TokenPayload
} from '@/utils/auth';
import { getCurrentUser } from '@/services/authService';
import { aclService } from '@/lib/api/aclService';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: TokenPayload | null;
  userACL: string | null;
  forbidden: string[];
}

/**
 * Lee el estado de auth desde localStorage de forma síncrona.
 * Permite que el primer render ya tenga userACL correcto sin esperar a verifyAuth.
 */
function getInitialAuthState(): AuthState {
  if (typeof window === 'undefined') {
    return { isAuthenticated: false, isLoading: true, user: null, userACL: null, forbidden: [] };
  }
  try {
    const currentUser = getCurrentUser();
    if (currentUser?.acl) {
      return {
        isAuthenticated: true,
        isLoading: true,
        user: {
          sub: currentUser.ownerID || currentUser.sub || '',
          email: currentUser.correo || currentUser.email || '',
          name: currentUser.name || currentUser.userName || '',
          acl: currentUser.acl,
          exp: 0,
        },
        userACL: currentUser.acl,
        forbidden: [],
      };
    }
  } catch {
    // localStorage no disponible; verifyAuth se encargará
  }
  return { isAuthenticated: false, isLoading: true, user: null, userACL: null, forbidden: [] };
}

export function useAuth() {
  const router = useRouter();
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState);

  const verifyAuth = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      let token = getToken();
      let payload: TokenPayload | null = null;

      if (token && !isTokenExpired(token)) {
        payload = decodeToken(token);
      }

      // Fallback: si no hay token válido pero hay currentUser en localStorage,
      // considerar autenticado (evita loop tras login exitoso cuando la cookie
      // aún no está disponible por timing/cross-origin)
      if (!payload && checkAuth()) {
        const currentUser = getCurrentUser();
        if (currentUser) {
          payload = {
            sub: currentUser.ownerID || currentUser.sub || '',
            email: currentUser.correo || currentUser.email || '',
            name: currentUser.name || currentUser.userName || '',
            acl: currentUser.acl || '',
            exp: 0, // No verificable sin token
          };
        }
      }

      if (!payload) {
        if (token && isTokenExpired(token)) {
          clearSession();
        }
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          userACL: null,
          forbidden: [],
        });
        return;
      }

      // Cargar permisos (forbidden) del rol del usuario (con timeout de 5s)
      let forbidden: string[] = [];
      if (payload.acl) {
        try {
          const aclPromise = aclService.getByName(payload.acl);
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('ACL timeout')), 5000)
          );
          const aclResp = await Promise.race([aclPromise, timeoutPromise]);
          if (aclResp.data) {
            forbidden = Array.isArray(aclResp.data.forbidden)
              ? aclResp.data.forbidden
              : Array.isArray(aclResp.data.forbidden_fields)
                ? aclResp.data.forbidden_fields
                : [];
          }
        } catch {
          // Si falla o timeout, continuar sin restricciones
        }
      }

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: payload,
        userACL: payload.acl || null,
        forbidden,
      });
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      clearSession();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        userACL: null,
        forbidden: [],
      });
    }
  }, []);

  useEffect(() => {
    verifyAuth();
    
    // Verificar periódicamente si el token expiró (cada 5 minutos)
    const interval = setInterval(() => {
      const token = getToken();
      if (token && isTokenExpired(token)) {
        handleLogout();
      }
    }, 300000); // Cada 5 minutos

    return () => clearInterval(interval);
  }, [verifyAuth]);

  const handleLogout = useCallback(() => {
    clearSession();
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      userACL: null,
      forbidden: [],
    });
    router.push('/login');
  }, [router]);

  const checkPermission = useCallback((requiredACL: string | string[]): boolean => {
    if (!authState.userACL) return false;
    return hasACL(authState.userACL, requiredACL);
  }, [authState.userACL]);

  const isMenuAllowed = useCallback((menuValue: string): boolean => {
    return !authState.forbidden.includes(menuValue);
  }, [authState.forbidden]);

  /**
   * Permiso por campo (ACL): asesorID, inheritedID, asesorNombre, ownerID, regionNombre.
   * ownerID y asesorID se tratan igual: si alguno está en forbidden, no se puede editar el campo "Asesor Origen".
   */
  const isFieldAllowed = useCallback((fieldKey: string): boolean => {
    const f = authState.forbidden || [];
    if (fieldKey === 'ownerID') {
      return !f.includes('ownerID') && !f.includes('asesorID');
    }
    return !f.includes(fieldKey);
  }, [authState.forbidden]);

  return {
    ...authState,
    logout: handleLogout,
    checkPermission,
    isMenuAllowed,
    isFieldAllowed,
    refreshAuth: verifyAuth,
  };
}
